/**
 * TaxAnalysisRulesPanel - Visar exakta AI-regler för skattekontoanalys
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { createTaxAnalysisPrompt, DEFAULT_COMPANY_CONFIG } from '@/lib/tax-calendar-helpers';
import { CompanyConfig } from '@/lib/tax-calendar-2025';
import { FileText, AlertCircle, Zap } from 'lucide-react';

interface TaxAnalysisRulesPanelProps {
  companyConfig?: CompanyConfig;
}

export function TaxAnalysisRulesPanel({ companyConfig = DEFAULT_COMPANY_CONFIG }: TaxAnalysisRulesPanelProps) {
  const fullPrompt = createTaxAnalysisPrompt(companyConfig);
  
  // Parse out key sections for better UI
  const sections = parsePromptSections(fullPrompt);

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <CardTitle>AI-Analysregler (Mini-Modell)</CardTitle>
        </div>
        <CardDescription>
          Exakta regler som {' '}
          <code className="px-1 py-0.5 bg-muted rounded text-xs">GPT-5-mini</code> 
          {' '} använder för att identifiera avvikelser i skattekontodata
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Företagskonfiguration:</strong> Storlek = {companyConfig.companySize === 'small' ? 'Liten' : 'Stor'}, 
            Momsperiod = {companyConfig.vatReportingPeriod === 'monthly' ? 'Månadsvis' : 'Kvartalsvis'}
          </AlertDescription>
        </Alert>

        <div className="space-y-6">
          {/* Domain-specific rules */}
          <div>
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-500" />
              Domänspecifika Regler
            </h3>
            <div className="space-y-1 text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
              {sections.domainRules.map((rule, i) => (
                <div key={i}>• {rule}</div>
              ))}
            </div>
          </div>

          {/* Exception rules */}
          {sections.exceptionRules.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                Undantagsregler
              </h3>
              <div className="space-y-1 text-sm bg-orange-50 dark:bg-orange-950/20 p-3 rounded-md border border-orange-200 dark:border-orange-800">
                {sections.exceptionRules.map((rule, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-orange-600 dark:text-orange-400 font-bold mt-0.5">•</span>
                    <span className="text-foreground">{rule}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analysis rules */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Detaljerade Analysregler</h3>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-4">
                {sections.analysisRules.map((rule, i) => (
                  <div key={i} className="border-l-2 border-primary/30 pl-3 py-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={getSeverityVariant(rule.priority)}>
                        {rule.priority}
                      </Badge>
                      <span className="font-medium text-sm">{rule.title}</span>
                    </div>
                    <ul className="text-xs text-muted-foreground space-y-0.5 ml-2">
                      {rule.details.map((detail, j) => (
                        <li key={j}>• {detail}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Critical application rules */}
          <div>
            <h3 className="text-sm font-semibold mb-2">Kritiska Tillämpningsregler</h3>
            <div className="space-y-1 text-sm bg-destructive/10 p-3 rounded-md border border-destructive/20">
              {sections.criticalRules.map((rule, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-destructive font-bold mt-0.5">•</span>
                  <span className="text-foreground">{rule}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tax calendar info */}
          {sections.taxCalendarInfo && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Skattekalender 2025</h3>
              <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md max-h-[200px] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-mono">{sections.taxCalendarInfo}</pre>
              </div>
            </div>
          )}

          <Separator />

          {/* Full prompt for debugging */}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Visa fullständig system-prompt (för debugging)
            </summary>
            <pre className="mt-2 p-3 bg-muted rounded-md overflow-x-auto whitespace-pre-wrap">
              {fullPrompt}
            </pre>
          </details>
        </div>
      </CardContent>
    </Card>
  );
}

function getSeverityVariant(priority: string): "default" | "destructive" | "outline" | "secondary" {
  switch (priority.toUpperCase()) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'default';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'outline';
    default:
      return 'outline';
  }
}

/**
 * Parsar system-prompten i strukturerade sektioner
 */
function parsePromptSections(prompt: string) {
  const lines = prompt.split('\n');
  
  const domainRules: string[] = [];
  const analysisRules: Array<{ title: string; priority: string; details: string[] }> = [];
  const criticalRules: string[] = [];
  const exceptionRules: string[] = [];
  let taxCalendarInfo = '';

  let currentSection = '';
  let currentRule: { title: string; priority: string; details: string[] } | null = null;

  for (const line of lines) {
    const trimmed = line.trim();
    
    // Detect sections
    if (trimmed.startsWith('Domänspecifikt')) {
      currentSection = 'domain';
      continue;
    } else if (trimmed.startsWith('UNDANTAGSREGEL FÖR PRELIMINÄRSKATT')) {
      currentSection = 'exception';
      continue;
    } else if (trimmed.startsWith('DETALJERADE ANALYSREGLER')) {
      currentSection = 'analysis';
      continue;
    } else if (trimmed.startsWith('KRITISKA TILLÄMPNINGSREGLER')) {
      currentSection = 'critical';
      continue;
    } else if (trimmed.startsWith('Skattekalender')) {
      currentSection = 'calendar';
      continue;
    } else if (trimmed.startsWith('Ignorera:') || trimmed.startsWith('Beräkning:') || trimmed.startsWith('Returnera endast')) {
      currentSection = '';
      continue;
    }

    // Parse domain rules
    if (currentSection === 'domain' && trimmed.startsWith('- ')) {
      domainRules.push(trimmed.substring(2));
    }

    // Parse exception rules
    if (currentSection === 'exception' && trimmed.startsWith('- ')) {
      exceptionRules.push(trimmed.substring(2));
    }

    // Parse analysis rules (numbered)
    if (currentSection === 'analysis') {
      const ruleMatch = trimmed.match(/^(\d+)\.\s+([A-ZÅÄÖ\s&]+)\s+\(priority:\s+(\w+)/i);
      if (ruleMatch) {
        if (currentRule) analysisRules.push(currentRule);
        currentRule = {
          title: ruleMatch[2].trim(),
          priority: ruleMatch[3].toUpperCase(),
          details: []
        };
      } else if (currentRule && trimmed.startsWith('- ')) {
        currentRule.details.push(trimmed.substring(2));
      } else if (currentRule && trimmed.startsWith('*')) {
        currentRule.details.push(trimmed.substring(1).replace(/^\*+/, '').trim());
      }
    }

    // Parse critical rules
    if (currentSection === 'critical' && trimmed.startsWith('- ')) {
      const cleaned = trimmed.substring(2).replace(/^\*+/, '').trim();
      if (cleaned) criticalRules.push(cleaned);
    }

    // Capture tax calendar info
    if (currentSection === 'calendar') {
      taxCalendarInfo += line + '\n';
    }
  }

  // Push last rule
  if (currentRule) analysisRules.push(currentRule);

  return {
    domainRules,
    analysisRules,
    criticalRules,
    exceptionRules,
    taxCalendarInfo: taxCalendarInfo.trim()
  };
}
